import { CommonModule, ViewportScroller } from '@angular/common';
import { AfterContentInit, Component, ElementRef, NO_ERRORS_SCHEMA, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { AutoFocusDirective } from '../directives/auto-focus.directive';
import { AppEnities, PHONE_REGEX, PHONE_REGEX_V2 } from '../common/constants.lib';
import { Subscription } from 'rxjs';
import { ControlParamsService } from './control-params.service';
import { ToastrService } from 'ngx-toastr';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import { all } from '@awesome.me/kit-787edf9aa0/icons';
import { MeService } from '../auth/me.service';
import { ControlParams, IControlParams } from '../models/models.lib';
import { ActivatedRoute } from '@angular/router';
import {
  SelectEvent,
  RemoveEvent,
  ErrorEvent,
  CancelEvent,
  PauseEvent,
  ResumeEvent,
  SuccessEvent,
  UploadEvent,
  UploadProgressEvent,
  FileInfo,
  UploadsModule,
  FileRestrictions,

} from '@progress/kendo-angular-upload';
import { SettingsService } from '../settings/settings.service';
import { SecurityService } from '../auth/security.service';
import { isNotNull } from '../common/utils.lib';
import { KENDO_TOOLTIPS } from '@progress/kendo-angular-tooltip';
import { Settings } from '../common/obj.lib';
import { LoggerService } from '../shared/logger.service';
import { environment } from '../../environments/environment';

const { CONTROL_PARAMS } = AppEnities;
const { apiUrl } = environment;

@Component({
  selector: 'casi-control-params',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputsModule,
    ButtonsModule,
    LabelModule,
    LayoutModule,
    AutoFocusDirective,
    FontAwesomeModule,
    UploadsModule,
    KENDO_TOOLTIPS
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './control-params.component.html',
  styleUrl: './control-params.component.scss'
})
export class ControlParamsComponent implements OnInit, OnDestroy, AfterContentInit {
  form: FormGroup;
  @ViewChild('pForm') pForm!: ElementRef;

  public rawData?: ControlParams = undefined;

  public canUpdate: boolean = false;
  public uploadSaveUrlBusiness: string = "businessLogoUpload";
  public uploadSaveUrlProduct: string = 'productLogoUpload';
  public uploadRemoveUrl: string = 'logoRemove';

  public hasBusinessLogo: boolean = true;
  public hasProductLogo: boolean = true;

  public logoProduct: string | null | undefined;
  public logoBusiness: string | null | undefined;
  public myRestrictions: FileRestrictions = {
    allowedExtensions: ["jpg", "jpeg", "png"],
  };
  private _subs: Subscription[] = [];
  // private settings: Settings = this.settingsService.getSettings();

  constructor(
    private logger: LoggerService,
    private fb: FormBuilder,
    private scroller: ViewportScroller,
    private dataService: ControlParamsService,
    private toastr: ToastrService,
    public faIcons: FaIconLibrary,
    private me: MeService,
    private route: ActivatedRoute,
    // private settingsService: SettingsService,
    private securityService: SecurityService
  ) {
    this.form = this.fb.group(
      {
        db_major_version: [null, [Validators.required]],
        db_minor_version: [null, [Validators.required]],
        send_payment_notification: [null, []],
        app_base_url: [null, []],
        use_live_payment_portal: [null, []],
        param_company_name: [null, []],
        param_entity_name: [null, []],
        param_company_address: [null, []],
        param_abn: [null, []],
        param_acn: [null, []],
        param_primary_email: [null, [Validators.email]],
        param_additional_email: [null, [Validators.email]],
        param_website_url: [null, []],
        param_primary_contact_number: [null, [Validators.pattern(PHONE_REGEX_V2)]],
        param_additional_contact_number: [null, [Validators.pattern(PHONE_REGEX_V2)]],
        param_primary_image: [null, []],
        param_additional_image: [null, []],
        param_product_name: [null, []],
        param_reply_to_email: [null, [Validators.email]],
        param_reset_password_bcc: [null, [Validators.email]],
        param_text_api_host: [null, []],
        param_text_api_port: [null, []],
        param_text_api_path: [null, []],
        param_text_api_user: [null, []],
        param_text_api_key: [null, []]
      }
    );
    faIcons.addIcons(...all);
    // this.settings = this.settingsService.getSettings();

  }

  //#region Form Properties

  get f() {
    return this.form.controls;
  }
  get db_major_version() {
    return this.form.get('db_major_version');
  }
  get db_minor_version() {
    return this.form.get('db_minor_version');
  }
  get send_payment_notification() {
    return this.form.get('send_payment_notification');
  }
  get app_base_url() {
    return this.form.get('app_base_url');
  }
  get use_live_payment_portal() {
    return this.form.get('use_live_payment_portal');
  }
  get param_company_name() {
    return this.form.get('param_company_name');
  }
  get param_entity_name() {
    return this.form.get('param_entity_name');
  }
  get param_company_address() {
    return this.form.get('param_company_address');
  }
  get param_abn() {
    return this.form.get('param_abn');
  }
  get param_acn() {
    return this.form.get('param_acn');
  }
  get param_primary_email() {
    return this.form.get('param_primary_email');
  }
  get param_additional_email() {
    return this.form.get('param_additional_email');
  }
  get param_website_url() {
    return this.form.get('param_website_url');
  }
  get param_primary_contact_number() {
    return this.form.get('param_primary_contact_number');
  }
  get param_additional_contact_number() {
    return this.form.get('param_additional_contact_number');
  }
  get param_primary_image() {
    return this.form.get('param_primary_image');
  }
  get param_additional_image() {
    return this.form.get('param_additional_image');
  }
  get param_product_name() {
    return this.form.get('param_product_name');
  }
  get param_reply_to_email() {
    return this.form.get('param_reply_to_email');
  }
  get param_reset_password_bcc() {
    return this.form.get('param_reset_password_bcc');
  }
  get param_text_api_host() {
    return this.form.get('param_text_api_host');
  }
  get param_text_api_port() {
    return this.form.get('param_text_api_port');
  }
  get param_text_api_path() {
    return this.form.get('param_text_api_path');
  }
  get param_text_api_user() {
    return this.form.get('param_text_api_user');
  }
  get param_text_api_key() {
    return this.form.get('param_text_api_key');
  }

  //#endregion

  //#region NG Event handlers

  ngAfterContentInit(): void {
    this.logger.debug('ngAfterContentInit...');
    setTimeout(() => {
      // document.body.scrollTo({ top: 0, behavior: 'smooth' });
      this.scroller.scrollToPosition([0, 0]);
      // this.pForm.nativeElement.scrollTop();
    }, 50);
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  ngOnInit(): void {
    this.logger.debug('ngOnInit...', CONTROL_PARAMS);
    const { can_update } = this.me.allICanDo(CONTROL_PARAMS)
    this.canUpdate = can_update;
    // let me = this.me.get();
    // this.canUpdate = me != null && me.admin != null ? me.admin : false;
   // this.logger.debug('ngOnInit:permissions: ', this.canUpdate, can_Update);
    // this.uploadSaveUrlBusiness = `${this.settings.apiUrl}/businessLogoUpload`;
    // this.uploadSaveUrlProduct = `${this.settings.apiUrl}/productLogoUpload`;
    // this.uploadRemoveUrl = `${this.settings.apiUrl}/logoRemove`; //not implemented
    this.uploadSaveUrlBusiness = `${apiUrl}/businessLogoUpload`;
    this.uploadSaveUrlProduct = `${apiUrl}/productLogoUpload`;
    this.uploadRemoveUrl = `${apiUrl}/logoRemove`; //not implemented

    this._subs.push(this.route.data.subscribe(({
      controlParams
    }) => {
      this.rawData = <IControlParams>controlParams;
      this.logger.debug(`ngOnInit:controlParams: `, this.rawData);
      this.setForm();
    }))

    this.param_primary_email?.valueChanges.subscribe(value => {
      if (value && value !== value.toLowerCase()) {
        this.param_primary_email?.setValue(value.toLowerCase(), { emitEvent: false });
      }
    });

    this.param_additional_email?.valueChanges.subscribe(value => {
      if (value && value !== value.toLowerCase()) {
        this.param_additional_email?.setValue(value.toLowerCase(), { emitEvent: false });
      }
    });

    this.param_reply_to_email?.valueChanges.subscribe(value => {
      if (value && value !== value.toLowerCase()) {
        this.param_reply_to_email?.setValue(value.toLowerCase(), { emitEvent: false });
      }
    });

    this.param_reset_password_bcc?.valueChanges.subscribe(value => {
      if (value && value !== value.toLowerCase()) {
        this.param_reset_password_bcc?.setValue(value.toLowerCase(), { emitEvent: false });
      }
    });
  }

  //#endregion

  //#region Public functions

  cancelChanges() {
    this.form.reset();
    this.setForm();
    this.notify('Form has been reset');
  }

  onSubmit() {
    this.logger.debug(`onSubmit...`);
    this.logger.debug(`onSubmit:form:`, this.form.value);
    this.dataService.update(this.form.value)
      .then(resp => {
        this.logger.debug(`onSubmit:resp: `, resp);
        this.notify('Control parameters have been updated.');
        // this.form.markAsPristine();
        this.togglePristine()
      })
      .catch(err => {
        this.logger.error(`onSubmit:error: `, err.message);
        this.notify(err.message, true);
      })
  }

  clearLogo(flag: any) {
    this.logger.debug(`clearLogo:flag: `, flag);
    if (flag === 'business') {
      this.form.patchValue({ param_primary_image: null });
      this.logoBusiness = null;
      this.hasBusinessLogo = false;
    } else {
      this.form.patchValue({ param_additional_image: null });
      this.logoProduct = null;
      this.hasProductLogo = false;
    }
    this.toggleDirty();
  }

  //#endregion

  //#region Upload event handlers

  //#region Business Logo

  public cancel(e: CancelEvent): void {
    this.logger.debug(`cancel event...`, e);
  }

  public clear(): void {
    this.log("clear event");
  }

  public complete(): void {
    this.log(`complete event`);
  }

  public error(e: ErrorEvent): void {
    this.log(`error event: ${e.files[0].name}`);
  }

  public blur(): void {
    this.log(`blur event`);
  }

  public focus(): void {
    this.log(`focus event`);
  }

  public pause(e: PauseEvent): void {
    this.log(`pause event ${e.file.name}`);
  }

  public resume(e: ResumeEvent): void {
    this.log(`resume event ${e.file.name}`);
  }

  public remove(e: RemoveEvent): void {
    this.log(`remove event: ${e.files[0].name}`);
    // this.clearLogo('business');
  }

  public select(e: SelectEvent): void {
    this.logger.debug(`select event`, e);
  }

  public success(e: SuccessEvent): void {
    this.logger.debug(`successBusiness event:`, e);
    if (e.files && e.operation === "upload") {
      let name = e.files[0].name;
      let img = (e.response as any).body.img;
      this.logger.debug(`success event ${name}`, e);
      // this.logger.debug(`success:img`, img);
      this.form.patchValue({ param_primary_image: img })
      this.logoBusiness = img;
      this.hasBusinessLogo = true;
      this.toggleDirty();
    } else {
      // this.clearLogo('business');
    }
  }

  public upload(e: UploadEvent): void {
    this.log(`upload event ${e.files[0].name}`);
    this.securityService.secureUploads(e, 'Logo uploading');
  }

  public uploadProgress(e: UploadProgressEvent): void {
    this.log(`uploadProgress event ${e.percentComplete}`);
  }

  public valueChange(e: FileInfo[]): void {
    this.logger.debug(`valueChange:e: `, e);
    // this.log(`valueChange event ${e[0].name}`);
  }

  //#endregion

  //#region Product Logo

  public cancelProduct(e: CancelEvent): void {
    // this.log(`cancelProduct event ${e.files[0].name}`);
    this.logger.debug(`cancel event...`, e);
    // this.log(`cancelProduct event...`);
  }

  public clearProduct(): void {
    this.log("clearProduct event");
  }

  public completeProduct(): void {
    this.log(`completeProduct event`);
  }

  public errorProduct(e: ErrorEvent): void {
    this.log(`errorProduct event: ${e.files[0].name}`);
  }

  public blurProduct(): void {
    this.log(`blurProduct event`);
  }

  public focusProduct(): void {
    this.log(`focusProduct event`);
  }

  public pauseProduct(e: PauseEvent): void {
    this.log(`pauseProduct event ${e.file.name}`);
  }

  public resumeProduct(e: ResumeEvent): void {
    this.log(`resumeProduct event ${e.file.name}`);
  }

  public removeProduct(e: RemoveEvent): void {
    this.log(`removeProduct event: ${e.files[0].name}`);
    // this.clearLogo('product');
  }

  public selectProduct(e: SelectEvent): void {
    this.logger.debug(`selectProduct event`, e);
  }

  public successProduct(e: SuccessEvent): void {
    this.logger.debug(`successProduct event:`, e);
    if (e.files && e.operation === "upload") {
      let name = e.files[0].name;
      let img = (e.response as any).body.img;
      this.logger.debug(`successProduct event ${name}`, e);
      this.form.patchValue({ param_additional_image: img });
      this.logoProduct = img;
      this.hasProductLogo = true;
      this.toggleDirty();
    } else {
      // this.clearLogo('product');
      // this.form.patchValue({ param_additional_image: undefined });
      // this.logoProduct = undefined;
      // this.hasProductLogo = false;
    }
  }

  public uploadProduct(e: UploadEvent): void {
    this.log(`uploadProduct event ${e.files[0].name}`);
    this.securityService.secureUploads(e, 'Product uploading');
  }

  public uploadProgressProduct(e: UploadProgressEvent): void {
    this.log(`uploadProgressProduct event ${e.percentComplete}`);
  }

  public valueChangeProduct(e: FileInfo[]): void {
    this.logger.debug(`valueChangeProduct:e: `, e);
    // this.log(`valueChange event ${e[0].name}`);
  }

  //#endregion

  private log(event: string): void {
    this.logger.debug(`log => ${event}`);
  }

  //#endregion

  //#region Private functions

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error) {
      this.toastr.error(msg);
    } else {
      this.toastr.success(msg);
    }
  }

  private toggleDirty() {
    this.form.markAsDirty();
  }
  private togglePristine() {
    this.form.markAsPristine();
  }

  private setForm() {
    this.logoBusiness = this.rawData?.param_primary_image;
    this.logoProduct = this.rawData?.param_additional_image;
    this.hasBusinessLogo = isNotNull(this.rawData?.param_primary_image);
    this.hasProductLogo = isNotNull(this.rawData?.param_additional_image);
    this.logger.debug('ngOnInit:has logs: ', this.hasBusinessLogo, this.hasProductLogo);
    this.form.patchValue(this.rawData!);
  }

  //#endregion
}
